import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Chip, 
  LinearProgress,
  Stack
} from '@mui/material';
import { 
  ArrowForward, 
  Edit, 
  CloudUpload, 
  CheckCircle,
  Pending,
  LocalShipping
} from '@mui/icons-material';
import { OrderData } from '../../../components/OrderCard';

interface ProjectCardProps {
  order: OrderData;
  onAction: (action: string, orderId: string) => void;
  onViewDetails: (orderId: string) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ order, onAction, onViewDetails }) => {
  const productName = order.items?.[0]?.name || 'Custom Art Project';
  const orderId = order.paypalOrderId;
  
  // Determine status color and icon
  const getStatusConfig = () => {
    switch(order.status) {
      case 'shipped':
      case 'delivered':
        return { color: 'success' as const, icon: <CheckCircle fontSize="small" />, label: 'Completed' };
      case 'awaiting-info':
        return { color: 'warning' as const, icon: <Pending fontSize="small" />, label: 'Action Required' };
      case 'in-progress':
        return { color: 'info' as const, icon: <LocalShipping fontSize="small" />, label: 'In Production' };
      default:
        return { color: 'default' as const, icon: <Pending fontSize="small" />, label: order.status };
    }
  };

  const statusConfig = getStatusConfig();
  
  // Determine Primary Action based on state (Simplified logic for MVP)
  const getPrimaryAction = () => {
    // 1. Identify Product Type upfront
    const isMissionary = order.items.some(item => {
        const name = (item.name || '').toLowerCase();
        const id = (item.productId || '').toLowerCase();
        return name.includes('mission') || 
               id.includes('mission') || 
               id.includes('mpa') ||
               name.includes('lds'); 
    });

    const isGenerational = order.items.some(item => {
        const name = (item.name || '').toLowerCase();
        const id = (item.productId || '').toLowerCase();
        return name.includes('generation') || 
               id.includes('generation') || 
               id.includes('ga-');
    });

    // If we have specific missing actions recorded
    if (order.actionsRequired && order.actionsRequired.length > 0) {
      const action = order.actionsRequired[0];
      if (action.includes('Tree')) return { label: 'Edit Family Tree', icon: <Edit />, action: 'family-tree' };
      
      // FIX: Redirect generic photo requests to specific forms if applicable
      if (action.includes('Photo')) {
          if (isMissionary) return { label: 'Mission Info', icon: <Edit />, action: 'mission-form' };
          return { label: 'Upload Photos', icon: <CloudUpload />, action: 'photos' };
      }
      
      if (action.includes('Mission')) return { label: 'Mission Info', icon: <Edit />, action: 'mission-form' };
      return { label: 'Resume Project', icon: <ArrowForward />, action: 'resume' };
    }

    // Default fallbacks based on Product Type if status calls for it
    // We check this for almost any status to ensure the button is available if they need to edit it
    if (!['shipped', 'delivered', 'canceled'].includes(order.status)) {
       // (isMissionary and isGenerational logic moved up)

       if (isMissionary) {
          return { label: 'Configure Missionary', icon: <Edit />, action: 'mission-form' };
       }
       if (isGenerational) {
          return { label: 'Complete Gen Form', icon: <Edit />, action: 'generational-form' };
       }
       
       if (order.status === 'awaiting-info') {
           return { label: 'Complete Details', icon: <Edit />, action: 'resume' };
       }
    }
    
    return null; // No primary action needed
  };

  const primaryAction = getPrimaryAction();

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
        },
        border: '1px solid rgba(255,255,255,0.5)',
      }}
    >
      {/* Header with ID and Status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          #{orderId.toUpperCase().slice(0, 8)}...
        </Typography>
        <Chip 
          icon={statusConfig.icon}
          label={statusConfig.label} 
          color={statusConfig.color} 
          size="small" 
          variant="outlined"
          sx={{ fontWeight: 'bold' }}
        />
      </Box>

      {/* Main Content */}
      <Box sx={{ mb: 2, flexGrow: 1 }}>
        <Typography variant="h6" sx={{ 
          fontFamily: '"Playfair Display", serif', 
          fontWeight: 700,
          lineHeight: 1.2,
          mb: 1
        }}>
          {productName}
        </Typography>
        
        {/* Progress Bar (Mocked for now) */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Project Progress</Typography>
            <Typography variant="caption" fontWeight="bold">
              {order.completionPercentage || 25}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={order.completionPercentage || 25} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.05)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundImage: 'linear-gradient(45deg, #1a237e 30%, #534bae 90%)',
              }
            }} 
          />
        </Box>
      </Box>

      {/* Actions Footer */}
      <Stack spacing={1} sx={{ mt: 'auto' }}>
        {primaryAction && (
          <Button
            variant="contained"
            fullWidth
            startIcon={primaryAction.icon}
            onClick={() => onAction(primaryAction.action, orderId)}
            sx={{
              backgroundColor: '#1a237e',
              color: 'white',
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(26, 35, 126, 0.25)',
              '&:hover': {
                backgroundColor: '#000051',
              }
            }}
          >
            {primaryAction.label}
          </Button>
        )}
        
        <Button
          variant={primaryAction ? "text" : "outlined"}
          fullWidth
          size="small"
          onClick={() => onViewDetails(orderId)}
          sx={{ 
            textTransform: 'none', 
            color: 'text.secondary',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          View Order Details
        </Button>
      </Stack>
    </Paper>
  );
};

export default ProjectCard;
